import { Injectable, inject } from "@angular/core";
import { AuthService } from "@core/auth.service";
import { getAuth } from "firebase/auth";
import posthog from "posthog-js";
import { take } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonService } from "./common/common.service";

/** Bounded props only — never email, passwords, tokens, or full payloads. */
export type PosthogEventProperties = Record<string, string | number | boolean | null | undefined>;

/**
 * DEV-only PostHog spike (HATDEV-100772).
 * Inert when `environment.posthog.apiKey` is missing. Failures never block the app.
 */
@Injectable({
  providedIn: "root",
})
export class PosthogService {
  private readonly authService = inject(AuthService);
  private readonly commonService = inject(CommonService);
  private initRequested = false;
  private initialized = false;
  private lastIdentifiedUser?: string;
  /** Session dedupe so /signup, /partial-registration, and GTM do not triple-fire. */
  private lastRegistrationCompletedUid?: string;
  private registrationEmitInFlight = false;
  /** In-memory + sessionStorage dedupe shared by both GTM service variants. */
  private lastPaymentSucceededDedupeKey?: string;
  private static readonly paymentSucceededDedupeStorageKey =
    "posthog_payment_succeeded_dedupe";

  private get apiKey(): string {
    return String((environment as { posthog?: { apiKey?: string } })?.posthog?.apiKey || "").trim();
  }

  private get apiHost(): string {
    return String(
      (environment as { posthog?: { apiHost?: string } })?.posthog?.apiHost || ""
    ).trim();
  }

  private get uiHost(): string | undefined {
    const host = String(
      (environment as { posthog?: { uiHost?: string } })?.posthog?.uiHost || ""
    ).trim();
    return host || undefined;
  }

  /** True when DEV config supplies a project token + EU host. */
  isConfigured(): boolean {
    return !!this.apiKey && !!this.apiHost;
  }

  init(): void {
    if (this.initRequested || typeof window === "undefined") {
      return;
    }
    if (!this.isConfigured()) {
      return;
    }
    this.initRequested = true;

    try {
      posthog.init(this.apiKey, {
        api_host: this.apiHost,
        ui_host: this.uiHost,
        persistence: "localStorage",
        // Explicit analytics only — disable implicit collection (task AC).
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        capture_performance: false,
        disable_session_recording: true,
        enable_recording_console_log: false,
        disable_surveys: true,
        rageclick: false,
        // Heatmaps / dead clicks / exception capture off when supported by SDK.
        capture_heatmaps: false,
        capture_dead_clicks: false,
        loaded: () => {
          this.initialized = true;
        },
      } as Parameters<typeof posthog.init>[1]);
      this.initialized = true;
    } catch (error) {
      this.initialized = false;
      this.authService.errorHandlerLogs("[PostHog] init failed (non-blocking)", error);
    }
  }

  /**
   * Identify with the stable internal user id after auth.
   * No alias/reset — matches current Mixpanel behaviour (documented variance).
   */
  identify(userId: string | null | undefined): void {
    if (!this.initialized || !this.isConfigured()) {
      return;
    }
    const uid = String(userId || "").trim();
    if (!uid || uid === "null" || uid === "undefined") {
      return;
    }
    if (this.lastIdentifiedUser === uid) {
      return;
    }
    try {
      posthog.identify(uid);
      this.lastIdentifiedUser = uid;
    } catch (error) {
      this.authService.errorHandlerLogs("[PostHog] identify failed (non-blocking)", error);
    }
  }

  capture(eventName: string, properties?: PosthogEventProperties): void {
    if (!this.initialized || !this.isConfigured() || !eventName) {
      return;
    }
    try {
      const cleaned = this.cleanProperties(properties);
      posthog.capture(eventName, cleaned);
    } catch (error) {
      this.authService.errorHandlerLogs(
        `[PostHog] capture failed for ${eventName} (non-blocking)`,
        error
      );
    }
  }

  /**
   * payment_succeeded with cross-GTM-service dedupe (sessionStorage).
   * Both gtm-segment and gtm-sagment call this so one purchase cannot double-fire
   * even if both Angular GTM services invoke GTM_CJ_conversion.
   * @returns true when the event was captured, false when deduped/skipped
   */
  emitPaymentSucceeded(
    properties: PosthogEventProperties,
    dedupeKey: string
  ): boolean {
    if (!this.isConfigured()) {
      return false;
    }
    if (!this.initialized) {
      this.init();
    }
    if (!this.initialized) {
      return false;
    }

    const key = String(dedupeKey || "").trim();
    if (key) {
      try {
        const prev = sessionStorage.getItem(PosthogService.paymentSucceededDedupeStorageKey);
        if (prev === key) {
          return false;
        }
        sessionStorage.setItem(PosthogService.paymentSucceededDedupeStorageKey, key);
      } catch {
        // private mode / quota — still attempt capture once this call
      }
      if (this.lastPaymentSucceededDedupeKey === key) {
        return false;
      }
      this.lastPaymentSucceededDedupeKey = key;
    }

    const customerId = properties?.c_customer_id;
    if (customerId != null && customerId !== "") {
      this.identify(String(customerId));
    }
    this.capture("payment_succeeded", properties);
    return true;
  }

  /**
   * Registration complete for /signup and /partial-registration (and GTM mirror).
   * Deduped per UID. Retries via getUid when Auth currentUser is not ready yet.
   */
  emitRegistrationCompleted(source: string, userId?: string | null): void {
    if (!this.isConfigured()) {
      return;
    }
    if (!this.initialized) {
      this.init();
    }
    if (!this.initialized) {
      return;
    }

    const resolved = this.resolveUidSync(userId);
    if (resolved) {
      this.captureRegistrationCompleted(source, resolved);
      return;
    }

    if (this.registrationEmitInFlight || this.lastRegistrationCompletedUid) {
      return;
    }
    this.registrationEmitInFlight = true;
    this.commonService
      .getUid("PosthogService.emitRegistrationCompleted")
      .pipe(take(1))
      .subscribe({
        next: (uid) => {
          this.registrationEmitInFlight = false;
          if (uid) {
            this.captureRegistrationCompleted(source, uid);
          } else {
            this.authService.errorHandlerLogs(
              "[PostHog] registration_completed skipped — uid unavailable",
              { source }
            );
          }
        },
        error: (error) => {
          this.registrationEmitInFlight = false;
          this.authService.errorHandlerLogs(
            "[PostHog] registration_completed getUid failed (non-blocking)",
            error
          );
        },
      });
  }

  private resolveUidSync(userId?: string | null): string {
    let uid = String(userId || "").trim();
    if (uid && uid !== "null" && uid !== "undefined") {
      return uid;
    }
    try {
      uid = String(getAuth()?.currentUser?.uid || "").trim();
    } catch {
      uid = "";
    }
    if (uid && uid !== "null" && uid !== "undefined") {
      return uid;
    }
    return "";
  }

  private captureRegistrationCompleted(source: string, userId: string): void {
    const uid = String(userId || "").trim();
    if (!uid || uid === "null" || uid === "undefined") {
      return;
    }
    if (this.lastRegistrationCompletedUid === uid) {
      return;
    }
    this.lastRegistrationCompletedUid = uid;
    this.identify(uid);
    this.capture("registration_completed", {
      c_customer_id: uid,
      source,
    });
  }

  /**
   * Feature-flag smoke test. Deterministic false when PostHog is off / flag missing / error.
   */
  isFeatureEnabled(flagKey: string): boolean {
    if (!this.initialized || !this.isConfigured() || !flagKey) {
      return false;
    }
    try {
      return !!posthog.isFeatureEnabled(flagKey);
    } catch (error) {
      this.authService.errorHandlerLogs(
        `[PostHog] isFeatureEnabled failed for ${flagKey} (non-blocking)`,
        error
      );
      return false;
    }
  }

  private cleanProperties(
    properties?: PosthogEventProperties
  ): Record<string, string | number | boolean | null> | undefined {
    if (!properties) {
      return undefined;
    }
    const out: Record<string, string | number | boolean | null> = {};
    Object.keys(properties).forEach((key) => {
      const value = properties[key];
      if (value === undefined) {
        return;
      }
      out[key] = value;
    });
    return out;
  }
}
